import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-width: 980px;
  width: 100%;
  padding: 2.5rem 2.75rem;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  
  img {
    max-height: 60px;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #1a202c;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #718096;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const CardLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;

  @media (min-width: 900px) {
    flex-direction: row;
    align-items: stretch;
  }
`;

const LeftColumn = styled.div`
  flex: 1.1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: 900px) {
    padding-right: 1.75rem;
    border-right: 1px solid #e2e8f0;
  }

  @media (max-width: 899px) {
    padding-bottom: 1.25rem;
    border-bottom: 1px solid #edf2f7;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;

  @media (min-width: 900px) {
    padding-left: 1.75rem;
  }
`;

const InfoBox = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoItem = styled.p`
  margin: 0.5rem 0;
  font-size: 0.9rem;
  color: #4a5568;
  
  strong {
    color: #2d3748;
  }
`;

const DocumentTypeBox = styled.div`
  background: #ebf8ff;
  border: 1px solid #90cdf4;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
  
  h3 {
    color: #2b6cb0;
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
  }
  
  p {
    color: #4a5568;
    margin: 0;
    font-size: 0.9rem;
  }
`;

const UploadArea = styled.div<{ isDragging: boolean }>`
  border: 2px dashed ${props => props.isDragging ? '#4299e1' : '#cbd5e0'};
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isDragging ? '#ebf8ff' : '#f7fafc'};
  
  &:hover {
    border-color: #4299e1;
    background: #ebf8ff;
  }
`;

const UploadIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const UploadText = styled.p`
  color: #4a5568;
  margin: 0 0 0.5rem 0;
`;

const UploadHint = styled.p`
  color: #a0aec0;
  font-size: 0.8rem;
  margin: 0;
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const FileIcon = styled.span`
  font-size: 2rem;
`;

const FileInfo = styled.div`
  flex: 1;
  
  p {
    margin: 0;
    font-size: 0.9rem;
    color: #2d3748;
  }
  
  span {
    font-size: 0.8rem;
    color: #718096;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #e53e3e;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.5rem;
  
  &:hover {
    color: #c53030;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease;
`;

const Message = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
  
  ${props => props.type === 'success' && `
    background: #c6f6d5;
    color: #22543d;
    border: 1px solid #9ae6b4;
  `}
  
  ${props => props.type === 'error' && `
    background: #fed7d7;
    color: #742a2a;
    border: 1px solid #feb2b2;
  `}
  
  ${props => props.type === 'info' && `
    background: #bee3f8;
    color: #2a4365;
    border: 1px solid #90cdf4;
  `}
`;

const ExpiryWarning = styled.div`
  background: #fefce8;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  color: #92400e;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e2e8f0;
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  p {
    margin-top: 1rem;
    color: #718096;
  }
`;

interface RequestDetails {
  applicationId: string;
  recipientName: string;
  documentType: string;
  customDocumentName: string | null;
  message: string;
  expiresAt: string;
}

const DocumentUploadPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestDetails, setRequestDetails] = useState<RequestDetails | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid upload link.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/document-upload/${token}`);
        const data = await response.json();

        if (!response.ok) {
          setError(data.message || 'Invalid or expired upload link.');
          setLoading(false);
          return;
        }

        setRequestDetails(data.request);
      } catch (err) {
        setError('Failed to validate upload link. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleFileSelect = (file: File) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and PDF files are allowed.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit.');
      return;
    }

    setError(null);
    setSelectedFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !token) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('document', selectedFile);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(`${API_BASE}/api/document-upload/${token}`, {
        method: 'POST',
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setUploadSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to upload document.');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getDocumentTypeName = () => {
    if (!requestDetails) return '';
    if (requestDetails.documentType === 'custom') {
      return requestDetails.customDocumentName || 'Document';
    }
    return requestDetails.documentType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTimeRemaining = () => {
    if (!requestDetails?.expiresAt) return '';
    const expiry = new Date(requestDetails.expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
  };

  if (loading) {
    return (
      <PageContainer>
        <Card>
          <LoadingSpinner>
            <div className="spinner" />
            <p>Validating your upload link...</p>
          </LoadingSpinner>
        </Card>
      </PageContainer>
    );
  }

  if (error && !requestDetails) {
    return (
      <PageContainer>
        <Card>
          <Title>Upload Link Issue</Title>
          <Message type="error">{error}</Message>
          <Subtitle>If you believe this is an error, please contact the administrator for a new upload link.</Subtitle>
        </Card>
      </PageContainer>
    );
  }

  if (uploadSuccess) {
    return (
      <PageContainer>
        <Card>
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
            <Title>Document Uploaded Successfully!</Title>
            <Subtitle>
              Your document has been received and will be reviewed by the administrator. 
              You will be notified if any further action is required.
            </Subtitle>
            <InfoBox>
              <InfoItem><strong>Application:</strong> {requestDetails?.applicationId}</InfoItem>
              <InfoItem><strong>Document:</strong> {getDocumentTypeName()}</InfoItem>
            </InfoBox>
          </div>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card>
        <CardLayout>
          <LeftColumn>
            <Logo>
              <img src="/assets/images/banner.png" alt="Upload Requested Document" />
            </Logo>
            
            <Title>Upload Requested Document</Title>
            <Subtitle>Please upload the document requested by the administrator</Subtitle>

            {requestDetails && (
              <>
                <InfoBox>
                  <InfoItem><strong>Application ID:</strong> {requestDetails.applicationId}</InfoItem>
                  {requestDetails.recipientName && (
                    <InfoItem><strong>Applicant:</strong> {requestDetails.recipientName}</InfoItem>
                  )}
                </InfoBox>

                <DocumentTypeBox>
                  <h3>📄 {getDocumentTypeName()}</h3>
                  {requestDetails.message && <p>{requestDetails.message}</p>}
                </DocumentTypeBox>

                <ExpiryWarning>
                  ⏰ {getTimeRemaining()}
                </ExpiryWarning>
              </>
            )}
          </LeftColumn>

          <RightColumn>
            {error && <Message type="error">{error}</Message>}

            {!selectedFile ? (
              <UploadArea
                isDragging={isDragging}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon>📁</UploadIcon>
                <UploadText>Drag and drop your file here, or click to browse</UploadText>
                <UploadHint>Accepted formats: JPEG, PNG, GIF, PDF (Max 10MB)</UploadHint>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                />
              </UploadArea>
            ) : (
              <FilePreview>
                <FileIcon>{selectedFile.type.includes('pdf') ? '📄' : '🖼️'}</FileIcon>
                <FileInfo>
                  <p>{selectedFile.name}</p>
                  <span>{formatFileSize(selectedFile.size)}</span>
                </FileInfo>
                <RemoveButton onClick={() => setSelectedFile(null)} title="Remove file">
                  ✕
                </RemoveButton>
              </FilePreview>
            )}

            {uploading && (
              <ProgressBar>
                <ProgressFill progress={uploadProgress} />
              </ProgressBar>
            )}

            <SubmitButton
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? `Uploading... ${uploadProgress}%` : 'Upload Document'}
            </SubmitButton>
          </RightColumn>
        </CardLayout>
      </Card>
    </PageContainer>
  );
};

export default DocumentUploadPage;
